import { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { Chip } from '../ui/Chip';
import { Helper } from '../ui/Helper';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useModalStore } from '../../stores/modal.store';
import { ClientType, ClientStatus, type CreateClientInput } from '@masamune/shared';

const TAG_OPTIONS = ['key-account', 'retainer', 'one-shot', 'prospect', 'fondimpresa', 'enterprise', 'dtc', 'regulated'];

/**
 * Definizioni per tipo cliente: quali campi mostrare/richiedere.
 * AZIENDA: P.IVA obbligatoria (società di capitali/persone).
 * PIVA: ditta individuale → P.IVA + CF del titolare obbligatori.
 * PRIVATO: persona fisica → solo CF obbligatorio, no P.IVA, no SDI.
 * PA: SDI obbligatorio (6 caratteri IPA), P.IVA o CF obbligatori.
 */
const TYPE_CONFIG = {
  AZIENDA:  {
    label: 'Azienda',
    sub: 'Società di capitali o persone (S.r.l., S.p.A., S.n.c., ...)',
    nameLabel: 'Ragione sociale',
    namePlaceholder: 'es. Aureliana S.r.l.',
    showVat: true, vatRequired: true,
    showTaxCode: true, taxRequired: false,
    showSdi: true, sdiRequired: false,
    showPec: true,
  },
  PIVA: {
    label: 'Ditta individuale',
    sub: 'Libero professionista o ditta con P.IVA personale',
    nameLabel: 'Nome attività / titolare',
    namePlaceholder: 'es. Mario Rossi Studio',
    showVat: true, vatRequired: true,
    showTaxCode: true, taxRequired: true,
    showSdi: true, sdiRequired: false,
    showPec: true,
  },
  PRIVATO: {
    label: 'Privato',
    sub: 'Persona fisica senza P.IVA (consumer)',
    nameLabel: 'Nome e cognome',
    namePlaceholder: 'es. Mario Rossi',
    showVat: false, vatRequired: false,
    showTaxCode: true, taxRequired: true,
    showSdi: false, sdiRequired: false,
    showPec: false,
  },
  PA: {
    label: 'PA',
    sub: 'Pubblica Amministrazione (Comune, Regione, Ente)',
    nameLabel: 'Denominazione ente',
    namePlaceholder: 'es. Comune di Milano',
    showVat: true, vatRequired: false,
    showTaxCode: true, taxRequired: false,
    showSdi: true, sdiRequired: true,
    showPec: true,
  },
} as const;

export function NewClientModal({ onClose, clientId }: { onClose: () => void; clientId?: string }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const isEdit = !!clientId;
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  // Progressive disclosure one-path: in creazione mostriamo solo il percorso minimo
  // (tipo, nomi, fiscali obbligatori); in modifica apriamo tutto da subito.
  const [showExtra, setShowExtra] = useState(isEdit);
  const [form, setForm] = useState<Partial<CreateClientInput>>({
    type: ClientType.AZIENDA,
    status: ClientStatus.LEAD,
    tags: ['retainer'],
    contacts: [],
    country: 'IT',
    docLanguage: 'it',
  });

  // Edit: carica l'anagrafica completa (riusa la cache della scheda cliente) e
  // pre-compila il form UNA volta, così salvando non si azzerano i campi.
  const { data: editData } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.get<any>(`/clients/${clientId}`),
    enabled: isEdit,
  });
  const prefilled = useRef(false);
  useEffect(() => {
    if (!editData || prefilled.current) return;
    prefilled.current = true;
    setForm({
      type: editData.type,
      legalName: editData.legalName,
      shortName: editData.shortName,
      vat: editData.vat ?? undefined,
      taxCode: editData.taxCode ?? undefined,
      sdiCode: editData.sdiCode ?? undefined,
      pec: editData.pec ?? undefined,
      email: editData.email ?? undefined,
      phone: editData.phone ?? undefined,
      sector: editData.sector ?? undefined,
      website: editData.website ?? undefined,
      status: editData.status,
      paymentTerms: editData.paymentTerms ?? undefined,
      docLanguage: editData.docLanguage ?? 'it',
      internalNotes: editData.internalNotes ?? undefined,
      addressLine: editData.addressLine ?? undefined,
      city: editData.city ?? undefined,
      zip: editData.zip ?? undefined,
      province: editData.province ?? undefined,
      country: editData.country ?? 'IT',
      accountManagerId: editData.accountManagerId ?? undefined,
      sinceYear: editData.sinceYear ?? undefined,
      tags: (editData.tags ?? []).map((t: any) => t.name),
      contacts: (editData.contacts ?? []).map((co: any) => ({
        fullName: co.fullName, role: co.role ?? undefined, email: co.email ?? undefined,
        phone: co.phone ?? undefined, isPrimary: co.isPrimary,
      })),
    });
  }, [editData]);

  const config = TYPE_CONFIG[form.type ?? ClientType.AZIENDA];

  const mut = useMutation({
    mutationFn: (data: CreateClientInput) =>
      isEdit ? api.patch<{ id: string }>(`/clients/${clientId}`, data) : api.post<{ id: string }>('/clients', data),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      if (isEdit) {
        qc.invalidateQueries({ queryKey: ['client', clientId] });
        toast.ok('Cliente aggiornato', vars.legalName);
        onClose();
      } else {
        toast.ok('Cliente creato', vars.legalName);
        // Non chiudo: propongo il passo successivo del binario (primo progetto)
        setCreated({ id: res.id, name: vars.legalName });
      }
    },
    onError: (err) => toast.alert(isEdit ? 'Errore modifica cliente' : 'Errore creazione cliente', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const toggleTag = (t: string) => {
    const tags = form.tags ?? [];
    setForm({ ...form, tags: tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t] });
  };

  /**
   * Valida lato client gli stessi vincoli dello Zod superRefine: i bottoni
   * disabled comunicano subito all'utente cosa manca, prima del submit.
   */
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!form.legalName?.trim()) e.legalName = 'Obbligatorio';
    if (!form.shortName?.trim()) e.shortName = 'Obbligatorio';
    if (config.vatRequired && !form.vat?.trim()) e.vat = 'P.IVA obbligatoria';
    if (config.taxRequired && !form.taxCode?.trim()) e.taxCode = 'Codice fiscale obbligatorio';
    if (config.sdiRequired && !form.sdiCode?.trim()) e.sdiCode = 'Codice SDI/IPA obbligatorio';
    // PA: serve almeno uno tra P.IVA e CF
    if (form.type === ClientType.PA && !form.vat?.trim() && !form.taxCode?.trim()) {
      e.vat = 'P.IVA o CF della PA obbligatori';
    }
    return e;
  }, [form, config]);

  // Se un errore riguarda un campo fiscale nascosto dietro la disclosure
  // (es. PA senza P.IVA né CF), apriamo da soli i campi aggiuntivi così
  // l'utente vede subito cosa sistemare.
  useEffect(() => {
    if (showExtra) return;
    if ((errors.vat && !config.vatRequired) || (errors.taxCode && !config.taxRequired) || (errors.sdiCode && !config.sdiRequired)) {
      setShowExtra(true);
    }
  }, [errors, config, showExtra]);

  const submit = () => {
    if (Object.keys(errors).length) return;
    mut.mutate({
      ...form,
      legalName: form.legalName!,
      shortName: form.shortName!,
      tags: form.tags ?? [],
      contacts: form.contacts ?? [],
    } as CreateClientInput);
  };

  // Success screen (solo creazione) → next-best-action. DOPO tutti gli hook.
  if (created) {
    return (
      <Modal
        title="Cliente creato"
        kicker="CRM · Fatto"
        onClose={onClose}
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={onClose}>Solo chiudi</button>
            <div className="spacer" />
            <button
              className="btn btn-primary"
              onClick={() => { onClose(); openModal('newProject', { clientId: created.id }); }}
            >
              <Icon name="plus" size={12} /> Crea il primo progetto
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="mx-auto mb-3 grid place-items-center" style={{ width: 46, height: 46, borderRadius: 'var(--r-tile)', background: 'color-mix(in oklab, var(--ok) 14%, transparent)', color: 'var(--ok)', boxShadow: 'var(--hl-top)' }}>
            <Icon name="check" size={20} />
          </div>
          <div className="t-h3 mb-1">{created.name} è in anagrafica</div>
          <div className="mute text-[13px] max-w-[42ch] mx-auto leading-relaxed">
            Il passo logico è creare il <strong>primo progetto</strong> per tracciarne costi e marginalità —
            da lì potrai generare un preventivo da far firmare e poi la fattura, tutto collegato.
          </div>
        </div>
      </Modal>
    );
  }

  // ─── Campi fiscali condivisi ──────────────────────────────────────
  // Renderizzati nel blocco principale quando required per il tipo corrente,
  // altrimenti tra i "Campi aggiuntivi" (progressive disclosure one-path).

  // P.IVA: visibile solo per AZIENDA, PIVA, PA
  const vatField = config.showVat && (
    <Field
      label={
        <>
          Partita IVA{config.vatRequired && <span className="text-alert ml-0.5">*</span>}
          {form.type === ClientType.PA && (
            <Helper text="Per le PA è opzionale: spesso usano solo il Codice Fiscale ente. Almeno uno dei due è obbligatorio." />
          )}
        </>
      }
      span={4}
    >
      <input
        className="inp mono"
        aria-invalid={!!errors.vat}
        placeholder="IT00000000000"
        value={form.vat ?? ''}
        onChange={(e) => setForm({ ...form, vat: e.target.value })}
      />
      {errors.vat && <div className="field-error">{errors.vat}</div>}
    </Field>
  );

  // CF: visibile per tutti
  const taxCodeField = config.showTaxCode && (
    <Field
      label={
        <>
          Codice fiscale{config.taxRequired && <span className="text-alert ml-0.5">*</span>}
          {form.type === ClientType.PIVA && (
            <Helper text="Per ditte individuali serve il CF del titolare (quello personale), diverso dalla P.IVA dell'attività." />
          )}
        </>
      }
      span={4}
    >
      <input
        className="inp mono"
        aria-invalid={!!errors.taxCode}
        placeholder={form.type === ClientType.PRIVATO ? 'RSSMRA80A01H501Z' : ''}
        value={form.taxCode ?? ''}
        onChange={(e) => setForm({ ...form, taxCode: e.target.value })}
      />
      {errors.taxCode && <div className="field-error">{errors.taxCode}</div>}
    </Field>
  );

  // SDI: solo per AZIENDA, PIVA, PA
  const sdiField = config.showSdi && (
    <Field
      label={
        <>
          {form.type === ClientType.PA ? 'Codice IPA' : 'Codice SDI'}
          {config.sdiRequired && <span className="text-alert ml-0.5">*</span>}
          <Helper>
            {form.type === ClientType.PA ? (
              <>
                <strong>Codice IPA</strong>
                <div className="mt-1">Codice di 6 caratteri della PA destinataria (indicepa.gov.it). Obbligatorio per la fatturazione elettronica verso PA.</div>
              </>
            ) : (
              <>
                <strong>Codice SDI</strong>
                <div className="mt-1">7 caratteri per la fatturazione elettronica B2B. Se il cliente non te lo dà, puoi usare la PEC (sotto) o, in extremis, "0000000".</div>
              </>
            )}
          </Helper>
        </>
      }
      hint={form.type === ClientType.PA ? '6 caratteri IPA' : '7 caratteri SDI o 0000000'}
      span={4}
    >
      <input
        className="inp mono"
        aria-invalid={!!errors.sdiCode}
        maxLength={form.type === ClientType.PA ? 6 : 7}
        value={form.sdiCode ?? ''}
        onChange={(e) => setForm({ ...form, sdiCode: e.target.value.toUpperCase() })}
      />
      {errors.sdiCode && <div className="field-error">{errors.sdiCode}</div>}
    </Field>
  );

  // PEC: alternativa a SDI per B2B — mai required, sempre tra gli aggiuntivi
  const pecField = config.showPec && (
    <Field
      label={
        <>
          PEC
          <Helper text="Posta certificata: alternativa al codice SDI per ricevere le fatture elettroniche. Almeno uno dei due (SDI o PEC) è consigliato per B2B." />
        </>
      }
      span={6}
    >
      <input
        className="inp"
        type="email"
        placeholder="azienda@pec.it"
        value={form.pec ?? ''}
        onChange={(e) => setForm({ ...form, pec: e.target.value })}
      />
    </Field>
  );

  // Recapiti generali dell'azienda: la mail a cui si scrive e il numero che si
  // chiama. Non sono quelli del referente (che stanno nei contatti): servono
  // anche quando un referente non c'è, che è il caso della maggior parte
  // dell'anagrafica arrivata da FattureInCloud.
  const contattiFields = (
    <>
      <Field label="Email" span={6}>
        <input
          className="inp"
          type="email"
          placeholder="info@azienda.it"
          value={form.email ?? ''}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </Field>
      <Field label="Telefono" span={6}>
        <input
          className="inp"
          placeholder="+39 095 123456"
          value={form.phone ?? ''}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </Field>
    </>
  );

  return (
    <Modal
      title={isEdit ? 'Modifica cliente' : 'Nuovo cliente'}
      kicker="CRM · Anagrafica"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          {Object.keys(errors).length > 0 && (
            <span className="text-warn text-[13px] mr-2">
              Compila i campi obbligatori
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={mut.isPending || Object.keys(errors).length > 0}
          >
            {isEdit ? 'Salva modifiche' : <>Crea cliente <Icon name="arrowRight" size={12} /></>}
          </button>
        </>
      }
    >
      {/* ─── Tipo cliente ────────────────────────────────────────── */}
      <div className="form-section">
        <div className="form-section-hd">
          Tipo cliente
          <Helper>
            <strong>Che tipologia di cliente è?</strong>
            <div className="mt-1">La scelta cambia quali campi sono obbligatori (P.IVA, CF, SDI) e come il cliente viene registrato su FattureInCloud.</div>
            <div className="mt-2 space-y-1 text-[12px]">
              <div><strong>Azienda</strong>: società (S.r.l., S.p.A.). Serve P.IVA.</div>
              <div><strong>Ditta individuale</strong>: libero professionista con P.IVA propria. Serve P.IVA + CF del titolare.</div>
              <div><strong>Privato</strong>: persona fisica senza P.IVA. Serve solo CF.</div>
              <div><strong>PA</strong>: Pubblica Amministrazione. Serve codice IPA (6 caratteri) come SDI.</div>
            </div>
          </Helper>
        </div>
        <div className="seg-control">
          {([ClientType.AZIENDA, ClientType.PIVA, ClientType.PRIVATO, ClientType.PA] as const).map((t) => (
            <button
              key={t}
              aria-pressed={form.type === t}
              onClick={() => setForm({ ...form, type: t })}
              type="button"
            >
              {TYPE_CONFIG[t].label}
            </button>
          ))}
        </div>
        <div className="mute text-[13px] mt-2">{config.sub}</div>
      </div>

      {/* ─── Anagrafica ──────────────────────────────────────────── */}
      <div className="form-section">
        <div className="form-section-hd">Anagrafica</div>
        <div className="form-grid">
          <Field label={config.nameLabel} required span={8}>
            <input
              className="inp"
              aria-invalid={!!errors.legalName}
              placeholder={config.namePlaceholder}
              value={form.legalName ?? ''}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
            />
          </Field>
          <Field label="Nome breve" required span={4}>
            <input
              className="inp"
              aria-invalid={!!errors.shortName}
              placeholder="es. Aureliana"
              value={form.shortName ?? ''}
              onChange={(e) => setForm({ ...form, shortName: e.target.value })}
            />
          </Field>

          {/* Fiscali required per il tipo corrente: sempre visibili */}
          {config.vatRequired && vatField}
          {config.taxRequired && taxCodeField}
          {config.sdiRequired && sdiField}

          {showExtra && (
            <>
              {/* Fiscali opzionali + dati di contorno: dietro la disclosure */}
              {!config.vatRequired && vatField}
              {!config.taxRequired && taxCodeField}
              {!config.sdiRequired && sdiField}
              {pecField}
              {contattiFields}

              <Field label="Settore" span={config.showPec ? 6 : 12}>
                <input className="inp" value={form.sector ?? ''} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
              </Field>

              {/* Sito web: utile per Azienda/PIVA/PA, irrilevante per Privato */}
              {form.type !== ClientType.PRIVATO && (
                <Field label="Sito web" span={12}>
                  <input
                    className="inp"
                    placeholder="https://"
                    value={form.website ?? ''}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </Field>
              )}
            </>
          )}
        </div>

        {/* Disclosure one-path: il resto dell'anagrafica si apre solo a richiesta */}
        {!showExtra && (
          <button type="button" className="btn btn-ghost btn-sm mt-3" onClick={() => setShowExtra(true)}>
            + Campi aggiuntivi (email, telefono, settore, sito, tag, note)
          </button>
        )}
      </div>

      {showExtra && (
        <>
          {/* ─── Sede ──────────────────────────────────────────── */}
          <div className="form-section">
            <div className="form-section-hd">
              {form.type === ClientType.PRIVATO ? 'Residenza' : 'Sede'}
            </div>
            <div className="form-grid">
              <Field label="Indirizzo" span={8}><input className="inp" value={form.addressLine ?? ''} onChange={(e) => setForm({ ...form, addressLine: e.target.value })} /></Field>
              <Field label="CAP" span={4}><input className="inp mono" value={form.zip ?? ''} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></Field>
              <Field label="Città" span={6}><input className="inp" value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
              <Field label="Provincia" span={3}><input className="inp" maxLength={2} value={form.province ?? ''} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase() })} /></Field>
              <Field label="Paese" span={3}>
                <select className="inp" value={form.country ?? 'IT'} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                  <option>IT</option><option>EU</option><option>Extra-EU</option>
                </select>
              </Field>
            </div>
          </div>

          {/* ─── Tag & commerciale ──────────────────────────────────── */}
          <div className="form-section">
            <div className="form-section-hd">Tag & commerciale</div>
            <div className="form-grid">
              <Field label="Stato" span={6}>
                <div className="seg-control">
                  {[ClientStatus.LEAD, ClientStatus.ATTIVO, ClientStatus.IN_PAUSA].map((s) => (
                    <button key={s} aria-pressed={form.status === s} onClick={() => setForm({ ...form, status: s })} type="button">
                      {s.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Tag" span={12}>
                <div className="chip-picker">
                  {TAG_OPTIONS.map((t) => (
                    <Chip key={t} tone={form.tags?.includes(t) ? 'active' : 'ghost'} onClick={() => toggleTag(t)}>
                      {form.tags?.includes(t) && <Icon name="check" size={10} />} {t}
                    </Chip>
                  ))}
                </div>
              </Field>
              <Field label="Note interne" span={12}>
                <textarea className="inp" rows={2} value={form.internalNotes ?? ''} onChange={(e) => setForm({ ...form, internalNotes: e.target.value })} />
              </Field>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
